import { useReducer, useRef } from 'react';
import { ObservabilityEvent, ReportBlock } from '../../../types';
import { WebSocketService } from '../services/websocketService';
import { chatReducer, initialChatState } from './useChatReducer';
import { useChatWebSocket } from './useChatWebSocket';
import { useChatSessions } from './useChatSessions';
import { useChatStreaming } from './useChatStreaming';

export const useChat = (customerId?: string, initialInvestigationId?: string | null) => {
  const [state, dispatch] = useReducer(chatReducer, initialChatState);

  const wsRef = useRef<WebSocketService | null>(null);
  const activeSessionIdRef = useRef<string | null>(null);
  const streamingMessageRef = useRef<string>('');
  const streamingObservabilityRef = useRef<ObservabilityEvent[]>([]);
  const streamingReasoningRef = useRef<string>('');
  const streamingBlocksRef = useRef<ReportBlock[]>([]);

  useChatWebSocket({
    state,
    dispatch,
    customerId,
    wsRef,
    activeSessionIdRef,
    streamingMessageRef,
    streamingObservabilityRef,
    streamingReasoningRef,
    streamingBlocksRef,
  });

  const {
    selectSession,
    createNewSession,
    deleteSession,
    clearCurrentSession,
    refreshInvestigation,
    setCurrentSession,
    setSessions,
  } = useChatSessions({ dispatch, customerId, initialInvestigationId });

  const {
    sendMessage,
    cancelStream,
    clearConversation,
    editMessage,
  } = useChatStreaming({
    state,
    dispatch,
    wsRef,
    activeSessionIdRef,
    streamingMessageRef,
    streamingObservabilityRef,
    createNewSession,
    customerId,
  });

  return {
    currentSession: state.currentSession,
    setCurrentSession,
    sessions: state.sessions,
    setSessions,
    isLoading: state.status === 'loading',
    error: state.error,
    isAgentTyping: state.status === 'streaming',
    currentStreamingMessage: state.streamingMessage,
    agentActivity: state.agentActivity,
    connectionState: state.connectionState,
    operationStartTime: state.operationStartTime,
    timeoutWarning: state.timeoutWarning,
    contextUsage: state.contextUsage,
    messageQueue: state.messageQueue,
    sendMessage,
    cancelStream,
    clearConversation,
    createNewSession,
    selectSession,
    deleteSession,
    clearCurrentSession,
    editMessage,
    refreshInvestigation,
  };
};
